import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getSystemCategory, getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { tipoProximaLabel, type TipoProxima } from '@/lib/coberturaRevisiones';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  sistemaId: string;
  sistemaTipo: string;
  descripcion?: string;
  anclaPeriodo?: string | null;
  anclaTipo?: TipoProxima | null;
  onGuardado?: () => void;
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export const FijarPeriodoRevisionDialog: React.FC<Props> = ({
  open, onOpenChange, sistemaId, sistemaTipo, descripcion,
  anclaPeriodo, anclaTipo, onGuardado,
}) => {
  const { toast } = useToast();
  const hoy = new Date();
  const [mes, setMes] = useState(String(hoy.getMonth() + 1).padStart(2, '0'));
  const [anio, setAnio] = useState(String(hoy.getFullYear()));
  const [tipo, setTipo] = useState<TipoProxima>('anual');
  const [guardando, setGuardando] = useState(false);

  const esPci = getSystemCategory(sistemaTipo) === 'pci';

  const anios = useMemo(() => {
    const base = hoy.getFullYear();
    return Array.from({ length: 7 }, (_, i) => String(base - 1 + i));
  }, [hoy]);

  useEffect(() => {
    if (!open) return;
    const m = anclaPeriodo ? /^(\d{4})-(\d{2})$/.exec(anclaPeriodo) : null;
    if (m) {
      setAnio(m[1]);
      setMes(m[2]);
    } else {
      setAnio(String(hoy.getFullYear()));
      setMes(String(hoy.getMonth() + 1).padStart(2, '0'));
    }
    setTipo(anclaTipo && esPci ? anclaTipo : 'anual');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anclaPeriodo, anclaTipo, esPci]);

  const guardar = async (limpiar = false) => {
    setGuardando(true);
    const { error } = await (supabase as any)
      .from('sistemas')
      .update({
        revision_ancla_periodo: limpiar ? null : `${anio}-${mes}`,
        revision_ancla_tipo: limpiar ? null : (esPci ? tipo : 'anual'),
      })
      .eq('id', sistemaId);
    setGuardando(false);
    if (error) {
      toast({ title: 'Error', description: 'No se pudo guardar el periodo.', variant: 'destructive' });
      return;
    }
    toast({
      title: limpiar ? 'Periodo fijado eliminado' : 'Periodo de revisión fijado',
      description: limpiar
        ? 'El sistema vuelve a calcularse sólo con su historial.'
        : `Próxima revisión: ${MESES[parseInt(mes, 10) - 1]} ${anio}`,
    });
    onGuardado?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fijar próxima revisión</DialogTitle>
          <DialogDescription>
            {getSystemTypeLabel(sistemaTipo)}{descripcion ? ` · ${descripcion}` : ''}. Indica cuándo
            toca la próxima revisión para que el sistema entre en el ciclo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Mes</Label>
              <Select value={mes} onValueChange={setMes}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MESES.map((m, i) => (
                    <SelectItem key={m} value={String(i + 1).padStart(2, '0')}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Año</Label>
              <Select value={anio} onValueChange={setAnio}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {anios.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {esPci && (
            <div className="space-y-2">
              <Label>Qué revisión será</Label>
              <Select value={tipo} onValueChange={(v) => setTipo(v as TipoProxima)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(['anual', 'trimestral_1', 'trimestral_2', 'trimestral_3'] as TipoProxima[]).map(t => (
                    <SelectItem key={t} value={t}>{tipoProximaLabel(t)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                A partir de esa revisión se generan las siguientes cada 3 meses (1T, 2T, 3T y anual).
              </p>
            </div>
          )}

          {!esPci && (
            <p className="text-xs text-muted-foreground">
              Este sistema sólo lleva revisión anual: a partir de la fecha indicada se repetirá cada 12 meses.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {anclaPeriodo && (
            <Button variant="outline" onClick={() => guardar(true)} disabled={guardando}>
              Quitar fecha fijada
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={() => guardar(false)} disabled={guardando}>Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
